import React, { useEffect, useState } from 'react';
import { Box, Paper, Checkbox, FormControlLabel, Button, useTheme, Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { useCreateStudent } from '../../hooks/students/useCreateStudent';
import { useUpdateStudent } from '../../hooks/students/useUpdateStudent';
import { useGetSchools } from '../../hooks/schools/useGetSchools';
import { useGetGuardians } from '../../hooks/guardians/useGetGuardians';
import FormInput from '../inputs/FormInput';
import LoadingPage from '../global/LoadingPage';

const FormAddEditStudent = ({ currentData, isEdit = false }) => {
    const { i18n } = useTranslation();
    const theme = useTheme();
    const isArabic = i18n.language === 'ar';
    const isDark = theme.palette.mode === 'dark';
    const { enqueueSnackbar } = useSnackbar();

    const { createStudent, isPending: isCreating } = useCreateStudent();
    const { editStudent, isUpdate } = useUpdateStudent();
    const { isLoading: schoolsLoading, schools } = useGetSchools(1, 999);
    const { guardians: allGuardians } = useGetGuardians(1, 999);

    const loadingSubmit = isCreating || isUpdate;

    const [form, setForm] = useState({
        school_id: '', code: '', full_name: '', gender: '',
        phone: '', whatsapp: '', national_id: '',
        date_of_birth: '', address: '', memorized_parts_count: 0, is_active: true,
    });
    const [guardianIds, setGuardianIds] = useState([]);

    useEffect(() => {
        if (!isEdit || !currentData) return;
        setForm({
            school_id: currentData.school_id || '',
            code: currentData.code || '',
            full_name: currentData.full_name || '',
            gender: currentData.gender || '',
            phone: currentData.phone || '',
            whatsapp: currentData.whatsapp || '',
            national_id: currentData.national_id || '',
            date_of_birth: currentData.date_of_birth || '',
            address: currentData.address || '',
            memorized_parts_count: currentData.memorized_parts_count ?? 0,
            is_active: currentData.is_active ?? true,
        });
        if (currentData.guardians?.length) {
            setGuardianIds(currentData.guardians.map((g) => g.id));
        }
    }, [isEdit, currentData]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        if (type === 'checkbox') { setForm((p) => ({ ...p, [name]: checked })); return; }
        setForm((p) => ({ ...p, [name]: value }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const parts = Number(form.memorized_parts_count);
        if (isNaN(parts) || parts < 0 || parts > 30) {
            enqueueSnackbar(isArabic ? 'عدد الأجزاء يجب أن يكون بين 0 و 30' : 'Parts must be 0-30', { variant: 'error' });
            return;
        }
        const payload = {
            school_id: Number(form.school_id),
            code: form.code || null,
            full_name: form.full_name,
            gender: form.gender,
            phone: form.phone || null,
            whatsapp: form.whatsapp || null,
            national_id: form.national_id || null,
            date_of_birth: form.date_of_birth || null,
            address: form.address || null,
            memorized_parts_count: parts,
            is_active: !!form.is_active,
            guardian_ids: guardianIds.map(Number),
        };
        if (isEdit && currentData?.id) { editStudent({ req: payload, id: currentData.id }); }
        else { createStudent(payload); }
    };

    if (schoolsLoading) return <LoadingPage />;

    const schoolOpts = (schools || []).map((s) => ({ id: s.id, name: s.name }));
    const genderOpts = [
        { id: 'male', name: isArabic ? 'ذكر' : 'Male' },
        { id: 'female', name: isArabic ? 'أنثى' : 'Female' },
    ];
    const guardianOpts = (allGuardians || []).map((g) => ({ id: g.id, name: `${g.full_name}${g.relationship ? ` (${g.relationship})` : ''}` }));

    return (
        <Paper sx={{ p: 3, mt: 2, direction: isArabic ? 'rtl' : 'ltr', backgroundColor: isDark && 'transparent' }}>
            <form onSubmit={handleSubmit}>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                        <FormInput label={isArabic ? 'المدرسة' : 'School'} type="select" name="school_id"
                            value={form.school_id} onChange={(e) => setForm((p) => ({ ...p, school_id: e.target.value }))}
                            options={schoolOpts} />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <FormInput label={isArabic ? 'الاسم الكامل' : 'Full Name'}
                            placeholder={isArabic ? 'ادخل الاسم' : 'Enter name'}
                            type="text" name="full_name" value={form.full_name} onChange={handleChange} />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <FormInput label={isArabic ? 'الجنس' : 'Gender'} type="select" name="gender"
                            value={form.gender} onChange={(e) => setForm((p) => ({ ...p, gender: e.target.value }))}
                            options={genderOpts} />
                    </Grid>

                    <Grid item xs={12} md={3}>
                        <FormInput label={isArabic ? 'الكود' : 'Code'} placeholder="STD-001"
                            type="text" name="code" value={form.code} onChange={handleChange} required={false} />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <FormInput label={isArabic ? 'الهاتف' : 'Phone'}
                            type="text" name="phone" value={form.phone} onChange={handleChange} required={false} />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <FormInput label={isArabic ? 'واتساب' : 'WhatsApp'}
                            type="text" name="whatsapp" value={form.whatsapp} onChange={handleChange} required={false} />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <FormInput label={isArabic ? 'رقم البطاقة' : 'National ID'}
                            type="text" name="national_id" value={form.national_id} onChange={handleChange} required={false} />
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <FormInput label={isArabic ? 'تاريخ الميلاد' : 'Date of Birth'} type="date" name="date_of_birth"
                            value={form.date_of_birth} onChange={handleChange} required={false} />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <FormInput label={isArabic ? 'الأجزاء المحفوظة (0-30)' : 'Memorized Parts (0-30)'}
                            placeholder="0" type="number" name="memorized_parts_count"
                            value={form.memorized_parts_count} onChange={handleChange} required={false} />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <FormInput label={isArabic ? 'العنوان' : 'Address'}
                            type="text" name="address" value={form.address} onChange={handleChange} required={false} />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <FormInput label={isArabic ? 'أولياء الأمور' : 'Guardians'} type="multi_select" name="guardian_ids"
                            selectedValue={guardianIds}
                            handleSelectChange={(e) => setGuardianIds(e.target.value)}
                            options={guardianOpts} required={false} />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mt: { xs: 1, md: 3 } }}>
                            <FormControlLabel
                                control={<Checkbox checked={form.is_active} name="is_active" onChange={handleChange} size="small" />}
                                label={isArabic ? 'مفعّل' : 'Active'} />
                        </Box>
                    </Grid>
                </Grid>

                <Box mt={3} display="flex" justifyContent="flex-end" gap={1}>
                    <Button type="submit" variant="contained" color="primary" disabled={loadingSubmit}>
                        {loadingSubmit ? (isArabic ? 'جاري الحفظ...' : 'Saving...') : isEdit ? (isArabic ? 'تحديث' : 'Update') : (isArabic ? 'حفظ' : 'Save')}
                    </Button>
                </Box>
            </form>
        </Paper>
    );
};

export default FormAddEditStudent;
